import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Form, 
    Button, 
    Table, 
    Badge, 
    Alert, 
    Spinner, 
    Modal,
    Tabs,
    Tab,
    InputGroup,
    Pagination
} from 'react-bootstrap';
import { 
    Package, 
    User, 
    MapPin, 
    Clock, 
    Check, 
    AlertCircle, 
    Search,
    UserPlus,
    Truck,
    CheckCircle,
    Calendar,
    Phone,
    CreditCard,
    FileText,
    Trash2,
    XCircle,
    AlertTriangle,
    Copy,
    RefreshCw,
    Pause,
    Play,
    Filter,
    X
} from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";
import { getDeliveryManagement, getDeliveryAgents, assignDeliveryAgent, updateDeliveryStatus } from '../../actions/delivery';

const PAGE_SIZE = 20;

const TAB_STATUS = {
    pending: 'pending',
    scheduled: 'scheduled',
    assigned: 'assigned',
    inprogress: 'in_progress',
    delivered: 'delivered',
    cancelled: 'cancelled',
    failed: 'failed'
};

const INITIAL_FILTERS = { search: '', agentName: '', startDate: '', endDate: '' };

const AssignDeliveryAgent = ({ 
    deliveryData, 
    counts,
    pages,
    agents, 
    loading, 
    error,
    getDeliveryManagement, 
    getDeliveryAgents,
    assignDeliveryAgent,
    updateDeliveryStatus
}) => {
    const [activeTab, setActiveTab] = useState('pending');
    const [formData, setFormData] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
    const [page, setPage] = useState(1);
    const [dateError, setDateError] = useState('');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(() => {
        // Load auto-refresh preference from localStorage
        const saved = localStorage.getItem('deliveryAutoRefresh');
        return saved ? JSON.parse(saved) : false;
    });
    const [refreshInterval, setRefreshInterval] = useState(null);

    const fetchDeliveries = useCallback(() => (
        getDeliveryManagement({
            status: TAB_STATUS[activeTab],
            ...appliedFilters,
            page,
            limit: PAGE_SIZE
        })
    ), [getDeliveryManagement, activeTab, appliedFilters, page]);

    useEffect(() => {
        document.title = 'Delivery Management';
    }, []);

    // Fetch the active tab whenever the tab, filters or page change
    useEffect(() => {
        fetchDeliveries();
    }, [fetchDeliveries]);

    // Auto-refresh effect
    useEffect(() => {
        let interval = null;
        if (autoRefresh) {
            interval = setInterval(() => {
                fetchDeliveries();
            }, 30000); // 30 seconds
        }

        // Cleanup function
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [autoRefresh, fetchDeliveries]);

    const handleTabSelect = (key) => {
        setActiveTab(key);
        setPage(1);
    };

    const handleFilterChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const applyFilters = (e) => {
        e.preventDefault();
        if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
            setDateError('End date must be after start date');
            return;
        }
        setDateError('');
        setPage(1);
        setAppliedFilters(formData);
    };

    const clearFilters = () => {
        setPage(1);
        setDateError('');
        setFormData(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
    };

    const handleAssignOrder = (order) => {
        setSelectedOrder(order);
        // Call getDeliveryAgents with the order _id when Assign button is clicked
        getDeliveryAgents(order._id);
        setShowAssignModal(true);
    };

    const createGoogleMapsLink = (lat, lng) => {
        return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    };

    const handleCopyTransactionInfo = (order) => {
        // Copy the transaction info for delivery agent to the clipboard.
        // Copy Transaction ID, User name, phone, product name, address, filled cans, empty cans and gps coordinates for navigation
        const gpsLink = order.deliveryLocation?.coordinates
            ? createGoogleMapsLink(order.deliveryLocation.coordinates[0], order.deliveryLocation.coordinates[1])
            : 'N/A';
        const transactionInfo = `Transaction ID: ${order.transactionId}\nUser Name: ${order.user?.name || 'Deleted User'}\nPhone: ${order.user?.phone || 'N/A'}\nProduct Name: ${order.product?.name || 'N/A'}\nAddress: ${order.deliveryAddress?.fullAddress || 'N/A'}\nFilled Cans: ${order.filledCans}\nEmpty Cans: ${order.emptyCans}\nGPS Coordinates: ${gpsLink}`;
        navigator.clipboard.writeText(transactionInfo);
        // Show copied to clipboard message in dialog box
        setShowCopyDialog(true);
    };

    const toggleAutoRefresh = () => {
        const newAutoRefresh = !autoRefresh;
        setAutoRefresh(newAutoRefresh);
        // Save preference to localStorage
        localStorage.setItem('deliveryAutoRefresh', JSON.stringify(newAutoRefresh));
    };

    const confirmAssign = async () => {
        if (selectedOrder && selectedAgent) {
            // Call the assignDeliveryAgent action
            // Get agent info from agents object like freeAgents, busyAgent, refillAgents, lowInventoryAgents
            // loop through agents object and find the agent with the selectedAgent id
            let agent = null;
            Object.keys(agents).forEach(key => {
                if (agents[key].find(agent => agent._id === selectedAgent)) {
                    agent = agents[key].find(agent => agent._id === selectedAgent);
                }
            });
            const needRefill = agent.agentType === 'low_inventory' ? true : false;
            setShowAssignModal(false);
            setSelectedAgent('');
            setSelectedOrder(null);
            await assignDeliveryAgent(selectedOrder._id, selectedAgent, agent.agentType, agent.totalTime, needRefill);
            fetchDeliveries();
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const formatPhone = (phone) => {
        // Format phone number in IN +91 format
        if (phone && phone.length === 10) {
            return `+91 ${phone}`;
        }
        return phone;
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'warning',
            assigned: 'info',
            'in-progress': 'primary',
            scheduled: 'dark',
            delivered: 'success',
            cancelled: 'secondary',
            failed: 'danger'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
    };

    const renderPendingOrders = () => (
        <Card className="shadow-sm">
            <Card.Header className="bg-warning text-dark">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <AlertCircle size={20} className="me-2" />
                        Pending Deliveries ({counts.pending || 0})
                    </h5>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {deliveryData.pendingDelivery.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No pending deliveries found.
                    </Alert>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '15%' }}>Transaction ID</th>
                                <th style={{ width: '18%' }}>Customer</th>
                                <th style={{ width: '12%' }}>Product</th>
                                <th style={{ width: '15%' }}>Address</th>
                                <th style={{ width: '12%' }}>Cans Info</th>
                                <th style={{ width: '8%' }}>Deposit</th>
                                <th style={{ width: '12%' }}>Request Time</th>
                                <th style={{ width: '8%' }}>Action</th>
                                <th style={{ width: '8%' }}>Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveryData.pendingDelivery.map(order => (
                                // Check if order.user.name and order.user.phone are not null. If null set as deleted user
                                <tr key={order._id}>
                                    <td className="fw-bold">
                                        <FileText size={14} className="me-1 text-muted" />
                                        {order.transactionId}
                                    </td>
                                    <td>
                                        <div>
                                            <div className="fw-semibold">{order.user?.name || 'Deleted User'}</div>
                                            <small className="text-muted">
                                                <Phone size={12} className="me-1" />
                                                {formatPhone(order.user?.phone || 'N/A')}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Package size={14} className="me-1 text-muted" />
                                            <span className="fw-semibold">{order.product?.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-start">
                                            <MapPin size={14} className="me-1 mt-1 text-muted" />
                                            <small>{order.deliveryAddress?.fullAddress || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="small">
                                            <div><strong>Filled:</strong> {order.filledCans}</div>
                                            <div><strong>Empty:</strong> {order.emptyCans}</div>
                                            {order.damageCans > 0 && (
                                                <div className="text-danger"><strong>Damaged:</strong> {order.damageCans}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Badge bg={order.depositUsed ? 'success' : 'secondary'}>
                                            {order.depositUsed ? 'Used' : 'Not Used'}
                                        </Badge>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <Calendar size={12} className="me-1" />
                                            {formatDate(order.requestTime)}
                                        </small>
                                    </td>
                                    <td>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleAssignOrder(order)}
                                        >
                                            <UserPlus size={14} className="me-1" />
                                            Assign
                                        </Button>
                                    </td>
                                    <td>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleCopyTransactionInfo(order)}
                                        >
                                            <Copy size={14} className="me-1" />
                                            Copy
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    const renderScheduledOrders = () => (
        <Card className="shadow-sm">
            <Card.Header className="bg-dark text-white">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <Calendar size={20} className="me-2" />
                        Scheduled Deliveries ({counts.scheduled || 0})
                    </h5>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {deliveryData.scheduledDelivery.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No scheduled deliveries found.
                    </Alert>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '13%' }}>Transaction ID</th>
                                <th style={{ width: '16%' }}>Customer</th>
                                <th style={{ width: '11%' }}>Product</th>
                                <th style={{ width: '14%' }}>Address</th>
                                <th style={{ width: '11%' }}>Cans Info</th>
                                <th style={{ width: '12%' }}>Scheduled For</th>
                                <th style={{ width: '11%' }}>Action</th>
                                <th style={{ width: '12%' }}>Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveryData.scheduledDelivery.map(order => (
                                <tr key={order._id}>
                                    <td className="fw-bold">
                                        <FileText size={14} className="me-1 text-muted" />
                                        {order.transactionId}
                                    </td>
                                    <td>
                                        <div>
                                            <div className="fw-semibold">{order.user?.name || 'Deleted User'}</div>
                                            <small className="text-muted">
                                                <Phone size={12} className="me-1" />
                                                {formatPhone(order.user?.phone || 'N/A')}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Package size={14} className="me-1 text-muted" />
                                            <span className="fw-semibold">{order.product?.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-start">
                                            <MapPin size={14} className="me-1 mt-1 text-muted" />
                                            <small>{order.deliveryAddress?.fullAddress || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="small">
                                            <div><strong>Filled:</strong> {order.filledCans}</div>
                                            <div><strong>Empty:</strong> {order.emptyCans}</div>
                                            {order.damageCans > 0 && (
                                                <div className="text-danger"><strong>Damaged:</strong> {order.damageCans}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <Calendar size={12} className="me-1" />
                                            {order.scheduledFor ? formatDate(order.scheduledFor) : 'N/A'}
                                        </small>
                                    </td>
                                    <td>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleAssignOrder(order)}
                                        >
                                            <UserPlus size={14} className="me-1" />
                                            Assign
                                        </Button>
                                    </td>
                                    <td>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleCopyTransactionInfo(order)}
                                        >
                                            <Copy size={14} className="me-1" />
                                            Copy
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    const renderAssignedOrders = () => (
        <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <Truck size={20} className="me-2" />
                        Assigned Deliveries ({counts.assigned || 0})
                    </h5>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {deliveryData.assignedDelivery.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No assigned deliveries found.
                    </Alert>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Transaction ID</th>
                                <th>Customer</th>
                                <th>Product</th>
                                <th>Address</th>
                                <th>Assigned Agent</th>
                                <th>Assigned Time</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveryData.assignedDelivery.map(order => (
                                <tr key={order._id}>
                                    <td className="fw-bold">{order.transactionId}</td>
                                    <td>
                                        <div>
                                            <div className="fw-semibold">{order.user?.name || 'Deleted User'}</div>
                                            <small className="text-muted">
                                                <Phone size={12} className="me-1" />
                                                {formatPhone(order.user?.phone || 'N/A')}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Package size={14} className="me-1 text-muted" />
                                            <span>{order.product?.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-start">
                                            <MapPin size={14} className="me-1 mt-1 text-muted" />
                                            <small>{order.deliveryAddress?.fullAddress || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <User size={14} className="me-1 text-muted" />
                                            <span className="fw-semibold">{order.assignedAgent}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <Calendar size={12} className="me-1" />
                                            {formatDate(order.assignedTime)}
                                        </small>
                                        {order.scheduledFor && (
                                            <div>
                                                <small className="text-muted">
                                                    Scheduled: {formatDate(order.scheduledFor)}
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                    <td>{getStatusBadge(order.status)}</td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleAssignOrder(order)}
                                        >
                                            <UserPlus size={14} className="me-1" />
                                            Reassign
                                        </Button>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            onClick={() => handleCopyTransactionInfo(order)}
                                        >
                                            <Copy size={14} className="me-1" />
                                            Copy
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    const renderInProgressOrders = () => (
        <Card className="shadow-sm">
            <Card.Header className="bg-info text-white">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <Clock size={20} className="me-2" />
                        In Progress Deliveries ({counts.in_progress || 0})
                    </h5>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {deliveryData.inProgressDelivery.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No deliveries in progress.
                    </Alert>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Transaction ID</th>
                                <th>Customer</th>
                                <th>Product</th>
                                <th>Address</th>
                                <th>Assigned Agent</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveryData.inProgressDelivery.map(order => (
                                <tr key={order._id}>
                                    <td className="fw-bold">{order.transactionId}</td>
                                    <td>
                                        <div>
                                            <div className="fw-semibold">{order.user?.name || 'Deleted User'}</div>
                                            <small className="text-muted">
                                                <Phone size={12} className="me-1" />
                                                {formatPhone(order.user?.phone || 'N/A')}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Package size={14} className="me-1 text-muted" />
                                            <span>{order.product?.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-start">
                                            <MapPin size={14} className="me-1 mt-1 text-muted" />
                                            <small>{order.deliveryAddress?.fullAddress || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <User size={14} className="me-1 text-muted" />
                                            <span className="fw-semibold">{order.assignedAgent}</span>
                                        </div>
                                        {order.scheduledFor && (
                                            <small className="text-muted">
                                                Scheduled: {formatDate(order.scheduledFor)}
                                            </small>
                                        )}
                                    </td>
                                    <td>{getStatusBadge(order.status)}</td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleAssignOrder(order)}
                                        >
                                            <UserPlus size={14} className="me-1" />
                                            Reassign
                                        </Button>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            onClick={() => handleCopyTransactionInfo(order)}
                                        >
                                            <Copy size={14} className="me-1" />
                                            Copy
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    const renderDeliveredOrders = () => (
        <Card className="shadow-sm">
            <Card.Header className="bg-success text-white">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <CheckCircle size={20} className="me-2" />
                        Delivered Orders ({counts.delivered || 0})
                    </h5>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {deliveryData.deliveredDelivery.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No delivered orders found.
                    </Alert>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Transaction ID</th>
                                <th>Customer</th>
                                <th>Product</th>
                                <th>Address</th>
                                <th>Delivered By</th>
                                <th>Delivery Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveryData.deliveredDelivery.map(order => (
                                <tr key={order._id}>
                                    <td className="fw-bold">{order.transactionId}</td>
                                    <td>
                                        <div>
                                            <div className="fw-semibold">{order.user?.name || 'Deleted User'}</div>
                                            <small className="text-muted">
                                                <Phone size={12} className="me-1" />
                                                {formatPhone(order.user?.phone || 'N/A')}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Package size={14} className="me-1 text-muted" />
                                            <span>{order.product?.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-start">
                                            <MapPin size={14} className="me-1 mt-1 text-muted" />
                                            <small>{order.deliveryAddress?.fullAddress || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <User size={14} className="me-1 text-muted" />
                                            <span className="fw-semibold">{order.assignedAgent}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <Calendar size={12} className="me-1" />
                                            {formatDate(order.deliveredTime)}
                                        </small>
                                        {order.scheduledFor && (
                                            <div>
                                                <small className="text-muted">
                                                    Scheduled: {formatDate(order.scheduledFor)}
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    const renderCancelledOrders = () => (
        <Card className="shadow-sm">
            <Card.Header className="bg-secondary text-white">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <XCircle size={20} className="me-2" />
                        Cancelled Deliveries ({counts.cancelled || 0})
                    </h5>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {deliveryData.cancelledDelivery.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No cancelled deliveries found.
                    </Alert>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Transaction ID</th>
                                <th>Customer</th>
                                <th>Product</th>
                                <th>Address</th>
                                <th>Assigned Agent</th>
                                <th>Cancelled Time</th>
                                <th>Reason</th>
                                <th>Action</th>
                                <th>Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveryData.cancelledDelivery.map(order => (
                                <tr key={order._id}>
                                    <td className="fw-bold">{order.transactionId}</td>
                                    <td>
                                        <div>
                                            <div className="fw-semibold">{order.user?.name || 'Deleted User'}</div>
                                            <small className="text-muted">
                                                <Phone size={12} className="me-1" />
                                                {formatPhone(order.user?.phone || 'N/A')}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Package size={14} className="me-1 text-muted" />
                                            <span>{order.product?.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-start">
                                            <MapPin size={14} className="me-1 mt-1 text-muted" />
                                            <small>{order.deliveryAddress?.fullAddress || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <User size={14} className="me-1 text-muted" />
                                            <span className="fw-semibold">{order.assignedAgent || 'Not Assigned'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <Calendar size={12} className="me-1" />
                                            {formatDate(order.cancelledTime || order.updatedAt)}
                                        </small>
                                        {order.scheduledFor && (
                                            <div>
                                                <small className="text-muted">
                                                    Scheduled: {formatDate(order.scheduledFor)}
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            {order.cancellationReason || 'No reason provided'}
                                        </small>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleAssignOrder(order)}
                                        >
                                            <UserPlus size={14} className="me-1" />
                                            Reassign
                                        </Button>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            onClick={() => handleCopyTransactionInfo(order)}
                                        >
                                            <Copy size={14} className="me-1" />
                                            Copy
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    const renderFailedOrders = () => (
        <Card className="shadow-sm">
            <Card.Header className="bg-danger text-white">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <AlertTriangle size={20} className="me-2" />
                        Failed Deliveries ({counts.failed || 0})
                    </h5>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {deliveryData.failedDelivery.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No failed deliveries found.
                    </Alert>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Transaction ID</th>
                                <th>Customer</th>
                                <th>Product</th>
                                <th>Address</th>
                                <th>Assigned Agent</th>
                                <th>Failed Time</th>
                                <th>Failure Reason</th>
                                <th>Action</th>
                                <th>Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveryData.failedDelivery.map(order => (
                                <tr key={order._id}>
                                    <td className="fw-bold">{order.transactionId}</td>
                                    <td>
                                        <div>
                                            <div className="fw-semibold">{order.user?.name || 'Deleted User'}</div>
                                            <small className="text-muted">
                                                <Phone size={12} className="me-1" />
                                                {formatPhone(order.user?.phone || 'N/A')}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Package size={14} className="me-1 text-muted" />
                                            <span>{order.product?.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-start">
                                            <MapPin size={14} className="me-1 mt-1 text-muted" />
                                            <small>{order.deliveryAddress?.fullAddress || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <User size={14} className="me-1 text-muted" />
                                            <span className="fw-semibold">{order.assignedAgent || 'Not Assigned'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <Calendar size={12} className="me-1" />
                                            {formatDate(order.failedTime || order.updatedAt)}
                                        </small>
                                        {order.scheduledFor && (
                                            <div>
                                                <small className="text-muted">
                                                    Scheduled: {formatDate(order.scheduledFor)}
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <small className="text-danger">
                                            {order.failureReason || 'No reason provided'}
                                        </small>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleAssignOrder(order)}
                                        >
                                            <UserPlus size={14} className="me-1" />
                                            Reassign
                                        </Button>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            onClick={() => handleCopyTransactionInfo(order)}
                                        >
                                            <Copy size={14} className="me-1" />
                                            Copy
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    const renderPagination = () => {
        if (!pages || pages <= 1) return null;

        const startPage = Math.max(1, Math.min(page - 2, pages - 4));
        const endPage = Math.min(pages, startPage + 4);
        const items = [];
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === page} onClick={() => setPage(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center py-3">
                <Pagination className="mb-0">
                    <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
                    {items}
                    <Pagination.Next disabled={page === pages} onClick={() => setPage(page + 1)} />
                </Pagination>
            </div>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h2 className="mb-0">Delivery Management</h2>
                            <p className="text-muted">
                                All records
                                {loading && <Spinner animation="border" size="sm" variant="primary" className="ms-2" />}
                            </p>
                        </div>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            <Button
                                variant={autoRefresh ? "success" : "outline-secondary"}
                                size="sm"
                                onClick={toggleAutoRefresh}
                                className="d-flex align-items-center"
                            >
                                {autoRefresh ? (
                                    <>
                                        <Pause size={16} className="me-1" />
                                        Auto Refresh ON
                                    </>
                                ) : (
                                    <>
                                        <Play size={16} className="me-1" />
                                        Auto Refresh OFF
                                    </>
                                )}
                            </Button>
                            <Badge bg="warning" className="px-3 py-2">
                                59 Minute: {counts.pending || 0}
                            </Badge>
                            <Badge bg="dark" className="px-3 py-2">
                                Scheduled: {counts.scheduled || 0}
                            </Badge>
                            <Badge bg="primary" className="px-3 py-2">
                                Assigned: {counts.assigned || 0}
                            </Badge>
                            <Badge bg="info" className="px-3 py-2">
                                In Progress: {counts.in_progress || 0}
                            </Badge>
                            <Badge bg="success" className="px-3 py-2">
                                Delivered: {counts.delivered || 0}
                            </Badge>
                            <Badge bg="secondary" className="px-3 py-2">
                                Cancelled: {counts.cancelled || 0}
                            </Badge>
                            <Badge bg="danger" className="px-3 py-2">
                                Failed: {counts.failed || 0}
                            </Badge>
                        </div>
                    </div>
                    
                    <Form onSubmit={applyFilters} className="mb-4">
                        <Row className="g-2 align-items-end">
                            <Col md={4}>
                                <Form.Label className="small mb-1">Search</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Search size={16} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        placeholder="Customer, phone, transaction ID, product or address"
                                        value={formData.search}
                                        onChange={handleFilterChange}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">Agent</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="agentName"
                                    placeholder="Agent name"
                                    value={formData.agentName}
                                    onChange={handleFilterChange}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">From (request date)</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleFilterChange}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">To (request date)</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="endDate"
                                    value={formData.endDate}
                                    onChange={handleFilterChange}
                                />
                            </Col>
                            <Col md={2} className="d-flex gap-2">
                                <Button type="submit" variant="primary" disabled={loading}>
                                    <Filter size={14} className="me-1" />
                                    Apply
                                </Button>
                                <Button type="button" variant="outline-secondary" onClick={clearFilters} disabled={loading}>
                                    <X size={14} className="me-1" />
                                    Clear
                                </Button>
                            </Col>
                        </Row>
                        {dateError && <div className="text-danger small mt-1">{dateError}</div>}
                    </Form>
                </Col>
            </Row>

            {error && (
                <Alert variant="warning">
                    <AlertCircle size={20} className="me-2" />
                    {error}
                </Alert>
            )}

            <Tabs
                activeKey={activeTab}
                onSelect={handleTabSelect}
                className="mb-4"
            >
                <Tab eventKey="pending" title={
                    <span>
                        <AlertCircle size={16} className="me-2" />
                        59 Minute ({counts.pending || 0})
                    </span>
                }>
                    {renderPendingOrders()}
                </Tab>
                
                <Tab eventKey="scheduled" title={
                    <span>
                        <Calendar size={16} className="me-2" />
                        Scheduled ({counts.scheduled || 0})
                    </span>
                }>
                    {renderScheduledOrders()}
                </Tab>

                <Tab eventKey="assigned" title={
                    <span>
                        <Truck size={16} className="me-2" />
                        Assigned ({counts.assigned || 0})
                    </span>
                }>
                    {renderAssignedOrders()}
                </Tab>
                
                <Tab eventKey="inprogress" title={
                    <span>
                        <Clock size={16} className="me-2" />
                        In Progress ({counts.in_progress || 0})
                    </span>
                }>
                    {renderInProgressOrders()}
                </Tab>
                
                <Tab eventKey="delivered" title={
                    <span>
                        <CheckCircle size={16} className="me-2" />
                        Delivered ({counts.delivered || 0})
                    </span>
                }>
                    {renderDeliveredOrders()}
                </Tab>
                
                <Tab eventKey="cancelled" title={
                    <span>
                        <XCircle size={16} className="me-2" />
                        Cancelled ({counts.cancelled || 0})
                    </span>
                }>
                    {renderCancelledOrders()}
                </Tab>
                
                <Tab eventKey="failed" title={
                    <span>
                        <AlertTriangle size={16} className="me-2" />
                        Failed ({counts.failed || 0})
                    </span>
                }>
                    {renderFailedOrders()}
                </Tab>
            </Tabs>

            {renderPagination()}

            {/* Assign Agent Modal */}
            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Assign Delivery Agent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedOrder && (
                        <div>
                            <Card className="mb-3">
                                <Card.Header>
                                    <h6 className="mb-0">Delivery Details</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Transaction ID:</strong> {selectedOrder.transactionId}</p>
                                            <p><strong>Customer:</strong> {selectedOrder.user?.name || 'Deleted User'}</p>
                                            <p><strong>Phone:</strong> {formatPhone(selectedOrder.user?.phone || 'N/A')}</p>
                                            <p><strong>Product:</strong> {selectedOrder.product?.name || 'N/A'}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Address:</strong> {selectedOrder.deliveryAddress?.fullAddress || 'N/A'}</p>
                                            <p><strong>Filled Cans:</strong> {selectedOrder.filledCans}</p>
                                            <p><strong>Empty Cans:</strong> {selectedOrder.emptyCans}</p>
                                            <p><strong>Deposit Used:</strong> {selectedOrder.depositUsed ? 'Yes' : 'No'}</p>
                                        </Col>
                                    </Row>
                                    <p><strong>Request Time:</strong> {formatDate(selectedOrder.requestTime)}</p>
                                </Card.Body>
                            </Card>

                            <h6>Select Delivery Agent:</h6>
                            <Table responsive className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Select</th>
                                        <th>Agent Name</th>
                                        <th>Phone</th>
                                        <th>Status</th>
                                        <th>Active Deliveries</th>
                                        <th>Distance ( KM )</th>
                                        <th>Total Time ( Min )</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {agents.freeAgents && agents.freeAgents.map(agent => (
                                        <tr
                                            key={agent.id}
                                            role="button"
                                            onClick={() => setSelectedAgent(agent._id)}
                                            className={selectedAgent === agent._id ? 'table-primary' : ''}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td style={{ verticalAlign: 'middle' }} className="text-center" onClick={(e) => e.stopPropagation()}>
                                                <div className="d-flex justify-content-center align-items-center h-100">
                                                <Form.Check.Input
                                                    type="radio"
                                                    name="selectedAgent"
                                                    value={agent._id}
                                                    checked={selectedAgent === agent._id}
                                                    onChange={(e) => setSelectedAgent(e.target.value)}
                                                    className="m-0"
                                                    style={{ float: 'none', marginTop: 0 }}
                                                />
                                                </div>
                                            </td>
                                            <td className="fw-semibold">{agent.name}</td>
                                            <td>{agent.phone}</td>
                                            <td>
                                                <Badge bg="success">
                                                    AVAILABLE
                                                </Badge>
                                            </td>
                                            <td>{agent.activeDeliveries || 0}</td>
                                            <td>{agent.distance || 0}</td>
                                            <td>{agent.totalTime || 0}</td>
                                        </tr>
                                    ))}
                                    {agents.busyAgents && agents.busyAgents.map(agent => (
                                        <tr
                                            key={agent.id}
                                            role="button"
                                            onClick={() => setSelectedAgent(agent._id)}
                                            className={selectedAgent === agent._id ? 'table-primary' : 'table-secondary'}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td style={{ verticalAlign: 'middle' }} className="text-center" onClick={(e) => e.stopPropagation()}>
                                                <div className="d-flex justify-content-center align-items-center h-100">
                                                <Form.Check.Input
                                                    type="radio"
                                                    name="selectedAgent"
                                                    value={agent._id}
                                                    checked={selectedAgent === agent._id}
                                                    onChange={(e) => setSelectedAgent(e.target.value)}
                                                    className="m-0"
                                                    style={{ float: 'none', marginTop: 0 }}
                                                />
                                                </div>
                                            </td>
                                            <td className="fw-semibold">{agent.name}</td>
                                            <td>{agent.phone}</td>
                                            <td>
                                                <Badge bg="secondary">
                                                    BUSY
                                                </Badge>
                                            </td>
                                            <td>{agent.activeDeliveries || 0}</td>
                                            <td>{agent.distance || 0}</td>
                                            <td>{agent.totalTime || 0}</td>
                                        </tr>
                                    ))}
                                    {agents.refillAgents && agents.refillAgents.map(agent => (
                                        <tr
                                            key={agent.id}
                                            role="button"
                                            onClick={() => setSelectedAgent(agent._id)}
                                            className={selectedAgent === agent._id ? 'table-primary' : 'table-secondary'}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td style={{ verticalAlign: 'middle' }} className="text-center" onClick={(e) => e.stopPropagation()}>
                                                <div className="d-flex justify-content-center align-items-center h-100">
                                                <Form.Check.Input
                                                    type="radio"
                                                    name="selectedAgent"
                                                    value={agent._id}
                                                    checked={selectedAgent === agent._id}
                                                    onChange={(e) => setSelectedAgent(e.target.value)}
                                                    className="m-0"
                                                    style={{ float: 'none', marginTop: 0 }}
                                                />
                                                </div>
                                            </td>
                                            <td className="fw-semibold">{agent.name}</td>
                                            <td>{agent.phone}</td>
                                            <td>
                                                <Badge bg="secondary">
                                                    REFILL
                                                </Badge>
                                            </td>
                                            <td>{agent.activeDeliveries || 0}</td>
                                            <td>{agent.distance || 0}</td>
                                            <td>{agent.totalTime || 0}</td>
                                        </tr>
                                    ))}
                                    {agents.lowInventoryAgents && agents.lowInventoryAgents.map(agent => (
                                        <tr
                                            key={agent.id}
                                            role="button"
                                            onClick={() => setSelectedAgent(agent._id)}
                                            className={selectedAgent === agent._id ? 'table-primary' : 'table-secondary'}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td style={{ verticalAlign: 'middle' }} className="text-center" onClick={(e) => e.stopPropagation()}>
                                                <div className="d-flex justify-content-center align-items-center h-100">
                                                <Form.Check.Input
                                                    type="radio"
                                                    name="selectedAgent"
                                                    value={agent._id}
                                                    checked={selectedAgent === agent._id}
                                                    onChange={(e) => setSelectedAgent(e.target.value)}
                                                    className="m-0"
                                                    style={{ float: 'none', marginTop: 0 }}
                                                />
                                                </div>
                                            </td>
                                            <td className="fw-semibold">{agent.name}</td>
                                            <td>{agent.phone}</td>
                                            <td>
                                                <Badge bg="secondary">
                                                    LOW INVENTORY
                                                </Badge>
                                            </td>
                                            <td>{agent.activeDeliveries || 0}</td>
                                            <td>{agent.distance || 0}</td>
                                            <td>{agent.totalTime || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={confirmAssign}
                        disabled={!selectedAgent}
                    >
                        Assign Agent
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Copy Confirmation Dialog */}
            <Modal show={showCopyDialog} onHide={() => setShowCopyDialog(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <CheckCircle size={20} className="me-2 text-success" />
                        Success
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <p className="mb-0">Transaction information has been copied to clipboard!</p>
                        <small className="text-muted">You can now paste it to share with the delivery agent.</small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowCopyDialog(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

AssignDeliveryAgent.propTypes = {
    deliveryData: PropTypes.object,
    counts: PropTypes.object,
    pages: PropTypes.number,
    agents: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    getDeliveryManagement: PropTypes.func.isRequired,
    getDeliveryAgents: PropTypes.func.isRequired,
    assignDeliveryAgent: PropTypes.func.isRequired,
    updateDeliveryStatus: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    deliveryData: {
        pendingDelivery: state.delivery.pendingDelivery,
        assignedDelivery: state.delivery.assignedDelivery,
        inProgressDelivery: state.delivery.inProgressDelivery,
        scheduledDelivery: state.delivery.scheduledDelivery,
        deliveredDelivery: state.delivery.deliveredDelivery,
        cancelledDelivery: state.delivery.cancelledDelivery,
        failedDelivery: state.delivery.failedDelivery
    },
    counts: state.delivery.counts,
    pages: state.delivery.pages,
    agents: state.delivery.agents,
    loading: state.delivery.loading,
    error: state.delivery.error
});

export default connect(
    mapStateToProps, 
    { getDeliveryManagement, getDeliveryAgents, assignDeliveryAgent, updateDeliveryStatus }
)(AssignDeliveryAgent);